import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  LogOut,
  User,
  BarChart3,
  Users,
  TrendingUp,
  Package,
  Layout,
  UserCog,
  AlertCircle,
  Loader,
  KeyRound,
  ChevronDown,
  ChevronRight,
  Headphones,
  MapPin,
  Building2
} from 'lucide-react';
import { getUserMenuAccess, hasMenuAccess, isAdminUser } from '../config/userMenuConfig';
import { userService } from '../services/userService';
import DashboardComponent from './DashboardComponent';
import InvoiceReportComponent from './InvoiceReportComponent';
import SalesOverviewComponent from './SalesOverviewComponent';
import CustomerSummaryComponent from './CustomerSummaryComponent';
import CustomerRankingByProductTypeComponent from './CustomerRankingByProductTypeComponent';
import TopProductsComponent from './TopProductsComponent';
import ProductDetailsComponent from './ProductDetailsComponent';
import ProductDetailsBySalesTeamComponent from './ProductDetailsBySalesTeamComponent';
import ProductCategorySalesTeamComponent from './ProductCategorySalesTeamComponent';
import SalesTeamComponent from './SalesTeamComponent';
import CSReportComponent from './CSReportComponent';
import SalesMapByProvinceComponent from './SalesMapByProvinceComponent';
import ProductSalesTrendComponent from './ProductSalesTrendComponent';
import BundleProductsComponent from './BundleProductsComponent';
import TechnicianMapManagementComponent from './TechnicianMapManagementComponent';
import CustomerProductBreakdownComponent from './CustomerProductBreakdownComponent';
import CustomerSalesmanLookupComponent from './CustomerSalesmanLookupComponent';
import UnsoldProductsComponent from './UnsoldProductsComponent';
import SalesmanCustomerCountComponent from './SalesmanCustomerCountComponent';
import CorpErpComponent from './CorpErpComponent';
import ARDashboardComponent from './ARDashboardComponent';
import InactiveCreditCustomersComponent from './InactiveCreditCustomersComponent';

const MainMenuComponent = ({ userData, onLogout }) => {
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [showCustomerSubmenu, setShowCustomerSubmenu] = useState(false);
  const [showProductDetailsSubmenu, setShowProductDetailsSubmenu] = useState(false);
  const [showTopProductsSubmenu, setShowTopProductsSubmenu] = useState(false);
  const [showSalesTeamSubmenu, setShowSalesTeamSubmenu] = useState(false);
  const [permissionChecking, setPermissionChecking] = useState(false);
  const [permissionError, setPermissionError] = useState('');
  const [hasRealTimeAccess, setHasRealTimeAccess] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: ['', '', '', '', '', ''],
    newPassword: ['', '', '', '', '', ''],
    confirmPassword: ['', '', '', '', '', '']
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [currentStepPassword, setCurrentStepPassword] = useState(['', '', '', '', '', '']);
  const [tempPasswords, setTempPasswords] = useState({
    old: null,
    new: null,
    confirm: null
  });

  // Check user permissions from API
  const checkUserPermissions = useCallback(async (username) => {
    try {
      setPermissionChecking(true);
      setPermissionError('');
      
      // Simulate API call to check permissions
      const response = await fetch('https://apisaleeasy.psisat.com/api/admin-saleeasy/check-permissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username })
      });

      if (response.ok) {
        const result = await response.json();
        return result.hasFullAccess || false;
      } else {
        // Fallback to hardcoded list if API fails
        const allowedUsers = ['somporn', 'saritwat', 'maneenuch', 'suporn', 'pornchai', 'sudsawat'];
        return allowedUsers.includes(username.toLowerCase());
      }
    } catch (error) {
      console.error('Permission check error:', error);
      // Fallback to hardcoded list if API fails
      const allowedUsers = ['somporn', 'saritwat', 'maneenuch', 'suporn', 'pornchai', 'sudsawat'];
      return allowedUsers.includes(username.toLowerCase());
    } finally {
      setPermissionChecking(false);
    }
  }, []);

  // Check if user has access to specific menu using config
  const hasAccessToMenu = (menuId) => {
    const username = userData?.username || '';
    return hasMenuAccess(username, menuId);
  };

  // Check if user is admin (for legacy compatibility)
  const hasAccessToSalesOverview = () => {
    const username = userData?.username || '';
    return isAdminUser(username);
  };

  // Handle passcode input for current step
  const handleStepPasscodeInput = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    
    const newPassword = [...currentStepPassword];
    newPassword[index] = value;
    setCurrentStepPassword(newPassword);
    
    // Auto focus next input
    if (value && index < 5) {
      const nextInput = document.querySelector(`[data-step-field][data-index="${index + 1}"]`);
      if (nextInput) nextInput.focus();
    }
  };

  // Handle backspace navigation for current step
  const handleStepPasscodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !currentStepPassword[index] && index > 0) {
      const prevInput = document.querySelector(`[data-step-field][data-index="${index - 1}"]`);
      if (prevInput) prevInput.focus();
    }
  };

  // Handle numpad button click for current step
  const handleNumpadClick = (digit) => {
    // Find first empty position
    const emptyIndex = currentStepPassword.findIndex(val => val === '');
    if (emptyIndex === -1) return; // All filled
    
    const newPassword = [...currentStepPassword];
    newPassword[emptyIndex] = digit;
    setCurrentStepPassword(newPassword);
    
    // Auto focus next input
    if (emptyIndex < 5) {
      const nextInput = document.querySelector(`[data-step-field][data-index="${emptyIndex + 1}"]`);
      if (nextInput) nextInput.focus();
    }
  };

  // Handle backspace from numpad for current step
  const handleNumpadBackspace = () => {
    // Find last filled position
    const lastIndex = currentStepPassword.slice().reverse().findIndex(val => val !== '');
    if (lastIndex === -1) return; // All empty
    
    const actualIndex = 5 - lastIndex;
    
    const newPassword = [...currentStepPassword];
    newPassword[actualIndex] = '';
    setCurrentStepPassword(newPassword);
    
    // Focus the cleared input
    const input = document.querySelector(`[data-step-field][data-index="${actualIndex}"]`);
    if (input) input.focus();
  };

  // Handle step navigation
  const handleNextStep = () => {
    const currentPasswordStr = currentStepPassword.join('');
    
    if (currentPasswordStr.length !== 6) {
      setPasswordError('กรุณากรอกรหัสผ่านให้ครบ 6 หลัก');
      return;
    }

    if (currentStep === 1) {
      // Store old password
      setTempPasswords(prev => ({ ...prev, old: currentPasswordStr }));
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Store new password
      setTempPasswords(prev => ({ ...prev, new: currentPasswordStr }));
      setCurrentStep(3);
    } else if (currentStep === 3) {
      // Check if passwords match
      if (currentPasswordStr !== tempPasswords.new) {
        setPasswordError('รหัสผ่านใหม่ไม่ตรงกัน');
        return;
      }
      // Proceed to change password
      handlePasswordSubmit();
      return;
    }

    // Clear current input and error for next step
    setCurrentStepPassword(['', '', '', '', '', '']);
    setPasswordError('');
    
    // Focus first input of next step
    setTimeout(() => {
      const firstInput = document.querySelector('[data-step-field][data-index="0"]');
      if (firstInput) firstInput.focus();
    }, 100);
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setCurrentStepPassword(['', '', '', '', '', '']);
      setPasswordError('');
    }
  };

  const resetPasswordModal = () => {
    setCurrentStep(1);
    setCurrentStepPassword(['', '', '', '', '', '']);
    setTempPasswords({ old: null, new: null, confirm: null });
    setPasswordError('');
    setPasswordLoading(false);
  };

  // Handle password change submission
  const handlePasswordSubmit = async () => {
    setPasswordLoading(true);
    setPasswordError('');

    try {
      const result = await userService.changePassword(
        userData?.username,
        tempPasswords.old,
        tempPasswords.new
      );

      if (result.success) {
        alert('เปลี่ยนรหัสผ่านสำเร็จ');
        setShowChangePassword(false);
        resetPasswordModal();
      } else {
        setPasswordError(result.error || 'เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordError('เกิดข้อผิดพลาดในการเชื่อมต่อ');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Get current step information
  const getStepInfo = () => {
    switch (currentStep) {
      case 1:
        return {
          title: 'รหัสผ่านเดิม',
          subtitle: 'กรุณากรอกรหัสผ่านเดิมของคุณ',
          icon: '🔒'
        };
      case 2:
        return {
          title: 'รหัสผ่านใหม่',
          subtitle: 'กรุณากรอกรหัสผ่านใหม่',
          icon: '🆕'
        };
      case 3:
        return {
          title: 'ยืนยันรหัสผ่าน',
          subtitle: 'กรุณากรอกรหัสผ่านใหม่อีกครั้ง',
          icon: '✅'
        };
      default:
        return {
          title: 'เปลี่ยนรหัสผ่าน',
          subtitle: 'กรุณาดำเนินการตามขั้นตอน',
          icon: '🔑'
        };
    }
  };

  // Handle menu selection with permission check
  const handleMenuSelect = async (menuId) => {
    if (menuId === 'change-password') {
      setShowChangePassword(true);
      return;
    }

    // Convert menu IDs to config format
    const menuConfigMap = {
      'dashboard': 'dashboard',
      'invoice-report': 'invoiceReport',
      'sales-overview': 'salesOverview',
      'customer-summary': 'customerSummary',
      'customer-ranking-by-product-type': 'customerSummary',
      'customer-product-breakdown': 'customerSummary',
      'customer-salesman-lookup': 'customerSummary',
      'ar-dashboard': 'customerSummary',
      'inactive-credit-customers': 'customerSummary',
      'top-products': 'topProducts',
      'product-sales-trend': 'topProducts',
      'product-details': 'productDetails',
      'product-sales-team': 'productSalesTeam',
      'product-category-sales-team': 'productSalesTeam',
      'unsold-products': 'productDetails',
      'sales-team': 'salesTeam',
      'salesman-customer-count': 'salesTeam',
      'technician-map': 'technicianMap'
    };

    const configMenuId = menuConfigMap[menuId];
    
    // Check permissions using config
    if (configMenuId && !hasAccessToMenu(configMenuId)) {
      setPermissionError('คุณไม่มีสิทธิ์เข้าถึงเมนูนี้');
      return;
    }
    
    // Keep submenus open - only close them when selecting non-submenu items
    
    setSelectedMenu(menuId);
    setPermissionError('');
  };

  const baseMenuItems = [
    {
      id: 'dashboard',
      title: 'ภาพรวมระบบ',
      icon: Layout,
      color: 'from-slate-500 to-slate-600',
      onClick: () => handleMenuSelect('dashboard')
    },
    {
      id: 'invoice-report',
      title: 'รายงานการขาย',
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      onClick: () => handleMenuSelect('invoice-report')
    }
  ];

  const salesOverviewMenuItem = {
    id: 'sales-overview',
    title: 'พนักงานขาย',
    icon: BarChart3,
    color: 'from-green-500 to-green-600',
    onClick: () => handleMenuSelect('sales-overview')
  };

  const customerSummaryMenuItem = {
    id: 'customer-summary',
    title: 'อันดับลูกค้า',
    icon: Users,
    color: 'from-purple-500 to-purple-600',
    onClick: () => {
      setShowCustomerSubmenu(true);
    },
    hasSubmenu: true
  };

  const topProductsMenuItem = {
    id: 'top-products',
    title: 'สินค้าขายดี',
    icon: TrendingUp,
    color: 'from-orange-500 to-orange-600',
    onClick: () => {
      setShowTopProductsSubmenu(true);
    },
    hasSubmenu: true
  };

  const productDetailsMenuItem = {
    id: 'product-details',
    title: 'ข้อมูลสินค้ารายตัว',
    icon: Package,
    color: 'from-indigo-500 to-indigo-600',
    onClick: () => {
      setShowProductDetailsSubmenu(true);
    },
    hasSubmenu: true
  };

  const salesTeamMenuItem = {
    id: 'sales-team',
    title: 'ทีมขาย',
    icon: UserCog,
    color: 'from-emerald-500 to-emerald-600',
    onClick: () => setShowSalesTeamSubmenu(true),
    hasSubmenu: true
  };

  const technicianMapMenuItem = {
    id: 'technician-map',
    title: 'พิกัดแผนที่ตัวแทน',
    icon: MapPin,
    color: 'from-teal-500 to-teal-600',
    onClick: () => handleMenuSelect('technician-map')
  };

  const corpErpMenuItem = {
    id: 'corp-erp',
    title: 'Corp ERP Report',
    icon: Building2,
    color: 'from-indigo-500 to-indigo-700',
    onClick: () => handleMenuSelect('corp-erp')
  };

  const changePasswordMenuItem = {
    id: 'change-password',
    title: 'เปลี่ยนรหัสผ่าน',
    icon: KeyRound,
    color: 'from-amber-500 to-amber-600',
    onClick: () => handleMenuSelect('change-password')
  };

  // Build menu items based on user permissions from config
  const buildMenuItems = () => {
    const username = userData?.username || '';
    const userAccess = getUserMenuAccess(username);
    let items = [];
    
    // Add base menu items based on user permissions
    if (userAccess.dashboard === 1) {
      items.push(baseMenuItems[0]); // dashboard
    }
    if (userAccess.invoiceReport === 1) {
      items.push(baseMenuItems[1]); // invoice-report
    }
    
    // Add advanced menu items based on user permissions
    if (userAccess.salesOverview === 1) {
      items.push(salesOverviewMenuItem);
    }
    if (userAccess.customerSummary === 1) {
      items.push(customerSummaryMenuItem);
    }
    if (userAccess.topProducts === 1) {
      items.push(topProductsMenuItem);
    }
    if (userAccess.productDetails === 1) {
      items.push(productDetailsMenuItem);
    }
    if (userAccess.salesTeam === 1) {
      items.push(salesTeamMenuItem);
    }
    if (userAccess.technicianMap === 1) {
      items.push(technicianMapMenuItem);
    }
    if (userAccess.corpErp === 1) {
      items.push(corpErpMenuItem);
    }

    // Always add change password at the end
    items.push(changePasswordMenuItem);
    
    return items;
  };

  const menuItems = buildMenuItems();

  const handleLogoutConfirm = () => {
    setShowLogoutConfirm(false);
    onLogout();
  };

  const renderContent = () => {
    if (!selectedMenu) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <h3 className="text-xl font-medium mb-2">เลือกเมนูจากด้านซ้าย</h3>
            <p>กรุณาเลือกเมนูที่ต้องการใช้งาน</p>
          </div>
        </div>
      );
    }

    switch (selectedMenu) {
      case 'dashboard':
        return <DashboardComponent 
          onNavigateToTopProducts={() => handleMenuSelect('top-products')}
          onNavigateToSalesOverview={() => handleMenuSelect('sales-overview')}
          onNavigateToInactiveCredit={() => {
            setShowCustomerSubmenu(true);
            handleMenuSelect('inactive-credit-customers');
          }}
        />;
      case 'invoice-report':
        return <InvoiceReportComponent />;
      case 'sales-overview':
        return hasAccessToMenu('salesOverview') ? <SalesOverviewComponent /> : null;
      case 'customer-summary':
        return hasAccessToMenu('customerSummary') ? <CustomerSummaryComponent onBack={() => setSelectedMenu(null)} /> : null;
      case 'customer-ranking-by-product-type':
        return hasAccessToMenu('customerSummary') ? <CustomerRankingByProductTypeComponent onBack={() => setSelectedMenu(null)} /> : null;
      case 'top-products':
        return hasAccessToMenu('topProducts') ? <TopProductsComponent /> : null;
      case 'product-sales-trend':
        return hasAccessToMenu('topProducts') ? <ProductSalesTrendComponent /> : null;
      case 'bundle-products':
        return hasAccessToMenu('topProducts') ? <BundleProductsComponent /> : null;
      case 'product-details':
        return hasAccessToMenu('productDetails') ? <ProductDetailsComponent /> : null;
      case 'product-sales-team':
        return hasAccessToMenu('productSalesTeam') ? <ProductDetailsBySalesTeamComponent /> : null;
      case 'product-category-sales-team':
        return hasAccessToMenu('productSalesTeam') ? <ProductCategorySalesTeamComponent /> : null;
      case 'sales-team':
        return hasAccessToMenu('salesTeam') ? <SalesTeamComponent /> : null;
      case 'salesman-customer-count':
        return hasAccessToMenu('salesTeam') ? <SalesmanCustomerCountComponent /> : null;
      case 'unsold-products':
        return hasAccessToMenu('productDetails') ? <UnsoldProductsComponent /> : null;
      case 'technician-map':
        return hasAccessToMenu('technicianMap') ? <TechnicianMapManagementComponent currentUser={userData?.username} /> : null;
      case 'corp-erp':
        return hasAccessToMenu('corpErp') ? <CorpErpComponent /> : null;
      case 'sales-map-by-province':
        return hasAccessToMenu('customerSummary') ? <SalesMapByProvinceComponent /> : null;
      case 'customer-product-breakdown':
        return hasAccessToMenu('customerSummary') ? <CustomerProductBreakdownComponent /> : null;
      case 'customer-salesman-lookup':
        return hasAccessToMenu('customerSummary') ? <CustomerSalesmanLookupComponent /> : null;
      case 'ar-dashboard':
        return hasAccessToMenu('customerSummary') ? <ARDashboardComponent /> : null;
      case 'inactive-credit-customers':
        return hasAccessToMenu('customerSummary') ? <InactiveCreditCustomersComponent /> : null;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-lg border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <img src="/logo-psi2018.png" alt="PSI Logo" className="w-8 h-8 object-contain" />
            <h1 className="text-lg font-bold text-gray-900">PSI Smart Sale Report</h1>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 p-4 space-y-1">
          {menuItems.filter(item => item.id !== 'change-password').map((item) => {
            const IconComponent = item.icon;
            const isCustomerMenu = item.id === 'customer-summary';
            const isProductDetailsMenu = item.id === 'product-details';
            const isTopProductsMenu = item.id === 'top-products';
            const isSalesTeamMenu = item.id === 'sales-team';

            const customerChildren = ['customer-summary', 'customer-ranking-by-product-type', 'sales-map-by-province', 'customer-product-breakdown', 'customer-salesman-lookup', 'ar-dashboard'];
            const productDetailsChildren = ['product-details', 'product-sales-team', 'product-category-sales-team', 'unsold-products'];
            const topProductsChildren = ['top-products', 'product-sales-trend', 'bundle-products'];
            const salesTeamChildren = ['sales-team', 'salesman-customer-count'];

            const isActive =
              selectedMenu === item.id ||
              (isCustomerMenu && customerChildren.includes(selectedMenu)) ||
              (isProductDetailsMenu && productDetailsChildren.includes(selectedMenu)) ||
              (isTopProductsMenu && topProductsChildren.includes(selectedMenu)) ||
              (isSalesTeamMenu && salesTeamChildren.includes(selectedMenu));

            return (
              <div key={item.id}>
                <button
                  onClick={item.onClick}
                  disabled={permissionChecking}
                  className={`w-full flex items-center gap-3 px-4 py-1 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 group disabled:opacity-50 disabled:cursor-not-allowed ${
                    isActive ? 'bg-blue-50 border border-blue-200' : ''
                  }`}
                >
                  <div className="w-7 h-7 flex items-center justify-center group-hover:scale-105 transition-transform">
                    {permissionChecking && selectedMenu === item.id ? (
                      <Loader className="w-5 h-5 text-blue-600 animate-spin" />
                    ) : (
                      <IconComponent className="w-5 h-5 text-gray-900" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{item.title}</h3>
                    {permissionChecking && selectedMenu === item.id && (
                      <p className="text-xs text-blue-600">กำลังตรวจสอบสิทธิ์...</p>
                    )}
                  </div>
                  {item.hasSubmenu && (
                    <div className="ml-2">
                      {(isCustomerMenu && showCustomerSubmenu) || (isProductDetailsMenu && showProductDetailsSubmenu) || (isTopProductsMenu && showTopProductsSubmenu) || (isSalesTeamMenu && showSalesTeamSubmenu) ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                    </div>
                  )}
                </button>
                
                {/* Customer Summary Submenu */}
                {isCustomerMenu && showCustomerSubmenu && (
                  <div className="ml-8 mt-2 space-y-1">
                    <button
                      onClick={() => handleMenuSelect('customer-summary')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'customer-summary' ? 'bg-purple-50 border border-purple-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">อันดับลูกค้าทั้งหมด</span>
                    </button>
                    <button
                      onClick={() => handleMenuSelect('customer-ranking-by-product-type')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'customer-ranking-by-product-type' ? 'bg-purple-50 border border-purple-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">อันดับลูกค้าตามประเภทสินค้า</span>
                    </button>
                    <button
                      onClick={() => handleMenuSelect('sales-map-by-province')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'sales-map-by-province' ? 'bg-purple-50 border border-purple-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">แผนที่ยอดขายตามจังหวัด</span>
                    </button>
                    <button
                      onClick={() => handleMenuSelect('customer-product-breakdown')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'customer-product-breakdown' ? 'bg-purple-50 border border-purple-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">สัดส่วนสินค้าของลูกค้า</span>
                    </button>
                    <button
                      onClick={() => handleMenuSelect('customer-salesman-lookup')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'customer-salesman-lookup' ? 'bg-purple-50 border border-purple-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">ค้นหาเซลล์จากลูกค้า</span>
                    </button>
                    <button
                      onClick={() => handleMenuSelect('ar-dashboard')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'ar-dashboard' ? 'bg-purple-50 border border-purple-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">ลูกหนี้การค้า</span>
                    </button>
                    <button
                      onClick={() => handleMenuSelect('inactive-credit-customers')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'inactive-credit-customers' ? 'bg-purple-50 border border-purple-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">ลูกค้าที่ยังไม่ใช้เครดิต</span>
                    </button>
                  </div>
                )}
                
                {/* Top Products Submenu */}
                {isTopProductsMenu && showTopProductsSubmenu && (
                  <div className="ml-8 mt-2 space-y-1">
                    <button
                      onClick={() => handleMenuSelect('top-products')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'top-products' ? 'bg-orange-50 border border-orange-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">สินค้าขายดี</span>
                    </button>
                    <button
                      onClick={() => handleMenuSelect('product-sales-trend')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'product-sales-trend' ? 'bg-orange-50 border border-orange-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">แนวโน้มการขายสินค้ารายเดือน</span>
                    </button>
                    <button
                      onClick={() => handleMenuSelect('bundle-products')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'bundle-products' ? 'bg-orange-50 border border-orange-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">สินค้าจับเซต</span>
                    </button>
                  </div>
                )}

                {/* Sales Team Submenu */}
                {item.id === 'sales-team' && showSalesTeamSubmenu && (
                  <div className="ml-8 mt-2 space-y-1">
                    <button
                      onClick={() => handleMenuSelect('sales-team')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'sales-team' ? 'bg-emerald-50 border border-emerald-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">สรุปผลทีมขาย</span>
                    </button>
                    <button
                      onClick={() => handleMenuSelect('salesman-customer-count')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'salesman-customer-count' ? 'bg-emerald-50 border border-emerald-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">จำนวนลูกค้าต่อเซลล์</span>
                    </button>
                  </div>
                )}

              {/* Product Details Submenu */}
                {isProductDetailsMenu && showProductDetailsSubmenu && (
                  <div className="ml-8 mt-2 space-y-1">
                    <button
                      onClick={() => handleMenuSelect('product-details')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'product-details' ? 'bg-indigo-50 border border-indigo-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">รหัสสินค้าและลูกค้า</span>
                    </button>
                    <button
                      onClick={() => handleMenuSelect('product-sales-team')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'product-sales-team' ? 'bg-indigo-50 border border-indigo-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">สินค้ารายตัวทีมขาย</span>
                    </button>
                    <button
                      onClick={() => handleMenuSelect('product-category-sales-team')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'product-category-sales-team' ? 'bg-indigo-50 border border-indigo-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">หมวดสินค้าทีมขาย</span>
                    </button>
                    <button
                      onClick={() => handleMenuSelect('unsold-products')}
                      className={`w-full flex items-center gap-3 px-4 py-1.5 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 ${
                        selectedMenu === 'unsold-products' ? 'bg-indigo-50 border border-indigo-200' : ''
                      }`}
                    >
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">สินค้าที่ไม่ได้ถูกขาย</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer - Change Password */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => handleMenuSelect('change-password')}
            className="w-full flex items-center gap-3 px-4 py-1 text-left rounded-lg transition-all duration-200 hover:bg-gray-50 group"
          >
            <div className="w-7 h-7 flex items-center justify-center group-hover:scale-105 transition-transform">
              <KeyRound className="w-5 h-5 text-gray-900" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900">เปลี่ยนรหัสผ่าน</h3>
            </div>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Header */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">PSI Smart Sale Report</h2>
              <p className="text-gray-600">ระบบรายงานการขาย</p>
              
              {/* Permission Status */}
              {permissionChecking && (
                <div className="flex items-center gap-2 mt-2 text-blue-600">
                  <Loader className="w-4 h-4 animate-spin" />
                  <span className="text-sm">กำลังตรวจสอบสิทธิ์...</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-4">
              {/* User Info */}
              <div className="flex items-center gap-2 text-gray-700">
                <User className="w-5 h-5" />
                <span className="font-medium">{userData?.fullName || userData?.firstName}</span>
              </div>
              
              {/* Quick Logout Button */}
              <button
                onClick={() => setShowLogoutConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">ออกจากระบบ</span>
              </button>
            </div>
          </div>
        </div>

        {/* Permission Error Alert */}
        {permissionError && (
          <div className="mx-6 mt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div>
                <h4 className="text-red-800 font-medium">ไม่สามารถเข้าถึงได้</h4>
                <p className="text-red-600 text-sm">{permissionError}</p>
              </div>
              <button
                onClick={() => setPermissionError('')}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1">
          {renderContent()}
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 fade-in max-h-[90vh] overflow-y-auto">
            
            {/* Header with step indicator */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">{getStepInfo().icon}</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{getStepInfo().title}</h3>
              <p className="text-gray-600 text-sm mt-1">{getStepInfo().subtitle}</p>
              
              {/* Step progress */}
              <div className="flex justify-center mt-4 space-x-2">
                {[1, 2, 3].map((step) => (
                  <div
                    key={step}
                    className={`w-3 h-3 rounded-full ${
                      step === currentStep
                        ? 'bg-amber-500'
                        : step < currentStep
                        ? 'bg-green-500'
                        : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {passwordError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <p className="text-red-600 text-sm">{passwordError}</p>
                </div>
              )}

              {/* Current step password input */}
              <div>
                <div className="flex gap-2 justify-center">
                  {currentStepPassword.map((digit, index) => (
                    <input
                      key={index}
                      type="password"
                      value={digit}
                      onChange={(e) => handleStepPasscodeInput(index, e.target.value)}
                      onKeyDown={(e) => handleStepPasscodeKeyDown(index, e)}
                      data-step-field
                      data-index={index}
                      className="w-12 h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      maxLength="1"
                      disabled={passwordLoading}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>
              </div>

              {/* Numpad */}
              <div className="mt-6 bg-gray-50 rounded-lg p-4">
                <div className="text-center mb-3">
                  <p className="text-sm text-gray-600">
                    กรอกรหัสผ่าน 6 หลัก
                  </p>
                </div>
                
                <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => handleNumpadClick(num.toString())}
                      disabled={passwordLoading}
                      className="h-12 bg-white hover:bg-amber-50 border-2 border-gray-300 hover:border-amber-400 rounded-lg text-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-300"
                    >
                      {num}
                    </button>
                  ))}
                  
                  <div></div> {/* Empty space */}
                  
                  <button
                    type="button"
                    onClick={() => handleNumpadClick('0')}
                    disabled={passwordLoading}
                    className="h-12 bg-white hover:bg-amber-50 border-2 border-gray-300 hover:border-amber-400 rounded-lg text-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-300"
                  >
                    0
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleNumpadBackspace}
                    disabled={passwordLoading}
                    className="h-12 bg-red-50 hover:bg-red-100 border-2 border-red-300 hover:border-red-400 rounded-lg text-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-50 disabled:hover:border-red-300 flex items-center justify-center"
                  >
                    ⌫
                  </button>
                </div>
              </div>

              {/* Navigation buttons */}
              <div className="flex gap-3 mt-6">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={handlePreviousStep}
                    disabled={passwordLoading}
                    className="flex-1 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                  >
                    ← ย้อนกลับ
                  </button>
                )}
                
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword(false);
                    resetPasswordModal();
                  }}
                  className="flex-1 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                  disabled={passwordLoading}
                >
                  ยกเลิก
                </button>
                
                <button
                  type="button"
                  onClick={handleNextStep}
                  disabled={passwordLoading || currentStepPassword.join('').length !== 6}
                  className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passwordLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    currentStep === 3 ? 'เปลี่ยนรหัสผ่าน' : 'ถัดไป →'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 fade-in">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogOut className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">ยืนยันการออกจากระบบ</h3>
              <p className="text-gray-600 mb-6">คุณต้องการออกจากระบบหรือไม่?</p>
              
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={handleLogoutConfirm}
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
                >
                  ออกจากระบบ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainMenuComponent;